import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, useTheme } from '@mui/material'
import hipersendService from '../../services/hipersend'
import countryCodesLib from '../../libs/countryCodes'
import { ApiDataWrapperContext } from '../../contexts/ApiDataWrapper'
import CountryCodesModal from '../CountryCodesModal'
import ListaDeDestinatarios from '../shared/disparo/ListaDeDestinatarios'
import ResumoDaConferencia from '../shared/disparo/ResumoDaConferencia'
import { tokensDoDisparo } from '../shared/disparo/tokens'
import EscolhaDeModelo from './EscolhaDeModelo'
import MensagemDeTexto, { nomeDoArquivo } from './MensagemDeTexto'
import TesteNoSeuNumero from './TesteNoSeuNumero'
import TextoLivreDeTeste from './TextoLivreDeTeste'
import PainelDoDisparo from './PainelDoDisparo'
import ListaDeDisparos from './ListaDeDisparos'
import useConferenciaDoDisparo from './useConferenciaDoDisparo'
import { isMetaOfficialProvider, TEMPLATE_CATEGORY_LABELS } from '../../libs/metaTemplates'
import { parsearNumerosComPais } from '../../libs/destinatarios'
import { aplicarVariaveis, montarSnippetTemplate, variaveisDoTemplate } from '../../libs/scheduledMessages'
import { reais, numero, disparoAtivo } from '../../libs/disparos'
import '../shared/disparo/disparo.css'

// "Enviar mensagem" como disparo em massa, no desenho aprovado em 17/10/2026:
// três passos (quem recebe → mensagem → revisar) e um recibo fixo com o custo,
// o medidor de saldo e a única ação principal. O disparo é criado no master e
// executado no servidor — fechar o navegador não interrompe nada; a aba
// "Acompanhar" mostra o andamento.
//
// API Oficial: só modelo aprovado (qualquer idioma, variáveis do corpo). QR Code: texto
// livre com campos da lista e arquivo opcional, como a tela antiga.
// Na Oficial, o "Escrever mensagem livre (teste)" do passo 2 abre um envio
// avulso de texto para até 5 números — fora do disparo, sem recibo.

const BRASIL = countryCodesLib.codes.find((pais) => pais.code === 'BR')
const COLUNA = /\{\{\s*([\w.-]+)\s*\}\}/g

function novaChave() {
  const c = typeof window !== 'undefined' ? window.crypto : null
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
}

const ehGrupo = (d) => String(d.phone).includes('@g.us')

function descreverPara(lista, limitarA) {
  const grupos = lista.filter(ehGrupo).length
  const pessoas = lista.length - grupos
  const partes = []
  if (pessoas) partes.push(`${numero(pessoas)} ${pessoas === 1 ? 'cliente' : 'clientes'}`)
  if (grupos) partes.push(`${numero(grupos)} ${grupos === 1 ? 'grupo' : 'grupos'}`)
  const base = partes.join(' e ')
  return limitarA && limitarA < lista.length ? `${numero(limitarA)} de ${base}` : base
}

function colunasUsadas(textos) {
  const achadas = new Set()
  textos.forEach((t) => {
    for (const m of String(t || '').matchAll(COLUNA)) achadas.add(m[1])
  })
  return [...achadas]
}

function juntar(itens) {
  if (itens.length <= 1) return itens.join('')
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`
}

function Passo({ n, titulo, estado, resumo, onAbrir, children }) {
  const feito = estado === 'feito'
  return (
    <section className={`dp-passo ${estado}`}>
      <button
        type="button"
        className="dp-passo-cab"
        onClick={feito ? onAbrir : undefined}
        disabled={estado === 'espera'}
        aria-expanded={estado === 'aberto'}
        style={{ cursor: feito ? 'pointer' : 'default' }}
      >
        <span className="dp-num">{feito ? '✓' : n}</span>
        <span className="dp-passo-tit">{titulo}</span>
        {resumo && (
          <span className="dp-passo-res">
            {resumo}
            {feito && <span className="dp-alterar">Alterar</span>}
          </span>
        )}
      </button>
      {estado === 'aberto' && <div className="dp-passo-corpo">{children}</div>}
    </section>
  )
}

export default function MessageSender() {
  const { getCredentials, provider, linkRecarga, user, suspensaoOficial } = useContext(ApiDataWrapperContext)
  const { api, secret } = getCredentials()
  const theme = useTheme()
  const oficial = isMetaOfficialProvider(provider)
  const temFiltro = !!user?.plan?.functionalities?.includes('SmartFilter')

  const [aba, setAba] = useState('novo')
  const [passo, setPasso] = useState(1)

  // Passo 1
  const [lista, setLista] = useState([])
  const [colunas, setColunas] = useState([])
  const [pais, setPais] = useState(BRASIL)
  const [escolhendoPais, setEscolhendoPais] = useState(false)
  const [erroLista, setErroLista] = useState('')
  const [verInvalidos, setVerInvalidos] = useState(false)

  // Passo 2
  const [modelo, setModelo] = useState({ nome: '', valores: [], template: null })
  const [texto, setTexto] = useState('')
  const [arquivo, setArquivo] = useState('')
  const [erroModelos, setErroModelos] = useState('')
  // Texto livre de teste (só na Oficial): enquanto aberto, nada de disparo.
  const [modoLivre, setModoLivre] = useState(false)
  const testeLivre = oficial && modoLivre

  // Recibo e confirmação
  const [limitarA, setLimitarA] = useState(null)
  const [conferenciaDoServidor, setConferenciaDoServidor] = useState(null)
  const [confirmando, setConfirmando] = useState(false)
  const [chave, setChave] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [erroCriacao, setErroCriacao] = useState('')
  const enviandoAgora = useRef(false)
  const recibo = useRef(null)

  // Acompanhar
  const [disparos, setDisparos] = useState([])
  const [carregouDisparos, setCarregouDisparos] = useState(false)
  const [selecionado, setSelecionado] = useState(null)

  const destinatarios = useMemo(
    () => lista.map((d) => ({ phone: d.phone, name: d.name, vars: d.vars })),
    [lista]
  )
  const conteudo = useMemo(() => {
    // No teste livre não há disparo: sem conteúdo, a conferência nem começa.
    if (testeLivre) return null
    if (oficial) return modelo.nome ? { tipo: 'modelo', nome: modelo.nome, valores: modelo.valores } : null
    if (!texto.trim()) return null
    return arquivo.trim() ? { tipo: 'texto', message: texto, fileUrl: arquivo.trim() } : { tipo: 'texto', message: texto }
  }, [testeLivre, oficial, modelo.nome, modelo.valores, texto, arquivo])
  const pronto = !!conteudo && lista.length > 0

  // A conferência do master só começa com lista E mensagem: antes disso o
  // recibo mostraria "falta a mensagem" como se fosse erro.
  const { conferencia: conferenciaAoVivo, carregando, erro: erroConferencia, atualizar } = useConferenciaDoDisparo({
    api,
    secret,
    destinatarios: pronto ? destinatarios : [],
    conteudo: pronto ? conteudo : null,
    limitarA: limitarA || undefined
  })

  // Mudou a lista ou a mensagem: o limite "só os que cabem" e a conferência
  // devolvida por um 409 deixam de valer.
  const entrada = JSON.stringify({ destinatarios, conteudo })
  useEffect(() => {
    setLimitarA(null)
    setConferenciaDoServidor(null)
  }, [entrada])
  useEffect(() => { setConferenciaDoServidor(null) }, [limitarA])

  // Voltou para a aba (ex.: depois de pagar a recarga em outra): confere o
  // saldo de novo — sem isso o recibo seguia dizendo "não cobre".
  const conferirDeNovo = useCallback(() => {
    setConferenciaDoServidor(null)
    if (atualizar) atualizar()
  }, [atualizar])
  useEffect(() => {
    window.addEventListener('focus', conferirDeNovo)
    return () => window.removeEventListener('focus', conferirDeNovo)
  }, [conferirDeNovo])

  // A chave do disparo vale para ESTA lista + mensagem + limite: se a resposta
  // se perder no caminho e a pessoa tentar de novo, a mesma chave faz o
  // servidor devolver o disparo já criado em vez de criar outro. Só troca
  // quando a entrada muda ou depois de um disparo criado.
  const chaveDaEntrada = useRef({ entrada: null, chave: '' })
  const chaveParaEstaEntrada = () => {
    const daVez = `${entrada}|${limitarA || ''}`
    if (chaveDaEntrada.current.entrada !== daVez) chaveDaEntrada.current = { entrada: daVez, chave: novaChave() }
    return chaveDaEntrada.current.chave
  }

  const conferencia = conferenciaDoServidor || (pronto ? conferenciaAoVivo : null)
  const veredito = conferencia?.veredito

  const carregarDisparos = useCallback(async () => {
    try {
      setDisparos(await hipersendService.disparos.listar(api, secret))
    } catch (e) {
      // A lista é complemento; o andamento do disparo aberto continua.
    } finally {
      setCarregouDisparos(true)
    }
  }, [api, secret])

  useEffect(() => {
    if (api && secret) carregarDisparos()
  }, [api, secret, carregarDisparos])

  const ativos = disparos.filter((d) => disparoAtivo(d.status)).length
  const emFoco = selecionado || (disparos.find((d) => disparoAtivo(d.status)) || disparos[0])?.disparoId || null

  const atualizarNaLista = useCallback((detalhe) => {
    if (!detalhe) return
    setDisparos((atual) => {
      const id = String(detalhe.disparoId)
      const resumo = {
        disparoId: detalhe.disparoId,
        criadoEm: detalhe.criadoEm,
        mensagem: detalhe.mensagem,
        status: detalhe.status,
        total: detalhe.total,
        cursor: detalhe.cursor,
        canceledReason: detalhe.canceledReason
      }
      if (!atual.some((d) => String(d.disparoId) === id)) return [resumo, ...atual]
      return atual.map((d) => (String(d.disparoId) === id ? { ...d, ...resumo } : d))
    })
  }, [])

  const recomecar = () => {
    setLista([])
    setColunas([])
    setModelo({ nome: '', valores: [], template: null })
    setTexto('')
    setArquivo('')
    setModoLivre(false)
    setLimitarA(null)
    setConferenciaDoServidor(null)
    setPasso(1)
  }

  const abrirConfirmacao = () => {
    setChave(chaveParaEstaEntrada())
    setErroCriacao('')
    setConfirmando(true)
  }

  const criar = async () => {
    if (enviandoAgora.current) return
    enviandoAgora.current = true
    setEnviando(true)
    setErroCriacao('')
    try {
      const r = await hipersendService.disparos.criar(api, secret, {
        destinatarios,
        conteudo,
        chaveIdempotencia: chave,
        limitarA: limitarA || undefined
      })
      setConfirmando(false)
      chaveDaEntrada.current = { entrada: null, chave: '' }
      recomecar()
      setSelecionado(String(r.disparoId))
      setAba('acomp')
      carregarDisparos()
    } catch (e) {
      const dados = e?.response?.data || {}
      if (e?.response?.status === 409 && dados.conferencia) {
        // O servidor conferiu de novo e não liberou: o recibo passa a mostrar
        // o que ele viu (modelo que deixou de estar aprovado, saldo que mudou…).
        setConferenciaDoServidor(dados.conferencia)
        setConfirmando(false)
      } else {
        setErroCriacao(dados.message || 'Não foi possível criar o disparo agora. Tente de novo em instantes.')
      }
    } finally {
      enviandoAgora.current = false
      setEnviando(false)
    }
  }

  // ---------- textos derivados ----------
  const grupos = lista.filter(ehGrupo).length
  const quantos = conferencia?.destinatarios?.validos ?? (limitarA || lista.length)
  const rotuloDaAcao = !quantos
    ? 'Disparar'
    : grupos
      ? `Disparar para ${numero(quantos)} ${quantos === 1 ? 'contato' : 'contatos'}`
      : `Disparar para ${numero(quantos)} ${quantos === 1 ? 'cliente' : 'clientes'}`
  const para = lista.length ? descreverPara(lista, limitarA) : ''
  const nomeArquivo = arquivo.trim() ? nomeDoArquivo(arquivo.trim()) : ''
  const mensagemCurta = oficial
    ? modelo.nome
    : texto.trim() ? `Texto${nomeArquivo ? ` + ${nomeArquivo}` : ''}` : ''

  const camposDoModelo = modelo.template ? variaveisDoTemplate(modelo.template) : []
  const usadas = colunasUsadas(oficial ? camposDoModelo.map((n) => modelo.valores[n - 1]) : [texto])
  const mensagemNaRevisao = oficial
    ? `${modelo.nome}${camposDoModelo.length ? (usadas.length ? `, com ${juntar(usadas)} de cada cliente` : ', com textos fixos') : ''}`
    : `Texto${usadas.length ? ` com ${juntar(usadas)} de cada cliente` : ''}${nomeArquivo ? ` + ${nomeArquivo}` : ''}`
  const foraDoExpediente = (conferencia?.motivos || []).some((m) => m.codigo === 'fora_do_expediente_aviso')

  const primeiro = lista[0]
  const mensagemDeTeste = oficial
    ? (modelo.template
      ? montarSnippetTemplate(modelo.template, camposDoModelo.map((n) => aplicarVariaveis(modelo.valores[n - 1] || '', primeiro)))
      : '')
    : aplicarVariaveis(texto, primeiro)

  const categoria = String(modelo.template?.category || '').toUpperCase()
  const resumoDaMensagem = oficial
    ? <><b>{modelo.nome}</b>{TEMPLATE_CATEGORY_LABELS[categoria] ? ` · ${TEMPLATE_CATEGORY_LABELS[categoria]}` : ''}</>
    : <><b>Texto</b>{nomeArquivo ? ` + ${nomeArquivo}` : ''}</>

  const duplicados = Number(conferencia?.destinatarios?.duplicados) || 0
  const invalidos = conferencia?.destinatarios?.invalidos || []

  const estado = (n) => (passo === n ? 'aberto' : passo > n ? 'feito' : 'espera')
  const podeAgir = passo === 3

  // ---------- barra do celular ----------
  const insuficiente = veredito === 'saldo_insuficiente'
  const barra = (() => {
    if (insuficiente) {
      return {
        linha: 'Saldo não cobre',
        valor: `cabem ${numero(conferencia?.saldo?.cabem)} de ${numero(conferencia?.destinatarios?.validos)}`,
        negativo: true,
        botao: 'Ver opções',
        habilitado: true,
        acao: () => recibo.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
      }
    }
    const custo = conferencia?.custo
    const valor = conferencia?.canal === 'qr' || (custo && !custo.cobravel)
      ? 'Sem custo'
      : custo ? reais(custo.total) : 'custo a definir'
    return {
      linha: para ? `${para}${custo?.cobravel ? ' · até' : ''}` : 'Ninguém na lista ainda',
      valor,
      negativo: false,
      botao: 'Disparar',
      habilitado: podeAgir && veredito === 'liberado' && !carregando,
      acao: abrirConfirmacao
    }
  })()

  return (
    <div className="dp" style={tokensDoDisparo(theme)}>
      <CountryCodesModal
        open={escolhendoPais}
        onClose={() => setEscolhendoPais(false)}
        onSelect={(p) => { setEscolhendoPais(false); setPais(p) }}
      />

      <div className={`dp-tela${aba === 'acomp' ? ' dp-acomp-tela' : ''}`}>
        <div className="dp-cab">
          <div>
            <h2>Enviar mensagem</h2>
            <p>Dispare para uma lista de clientes e acompanhe a entrega.</p>
          </div>
          <div className="dp-seg" role="tablist" aria-label="Disparo">
            <button type="button" role="tab" aria-selected={aba === 'novo'} onClick={() => setAba('novo')}>
              Novo disparo
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={aba === 'acomp'}
              onClick={() => { setAba('acomp'); carregarDisparos() }}
            >
              Acompanhar{ativos > 0 && <em>{ativos}</em>}
            </button>
          </div>
        </div>

        {aba === 'novo' ? (
          <>
            <div className="dp-corpo">
              <div className="dp-passos">
                <Passo
                  n={1}
                  titulo="Quem recebe"
                  estado={estado(1)}
                  resumo={passo > 1 && (
                    <><b>{para}</b>{colunas.length ? ` · colunas ${juntar(colunas)}` : ''}</>
                  )}
                  onAbrir={() => setPasso(1)}
                >
                  {lista.length > 0 && (
                    <div className="dp-pronto">
                      <strong>{numero(lista.length)}</strong>
                      <span>
                        {grupos
                          ? (lista.length === 1 ? 'contato pronto para receber' : 'contatos prontos para receber')
                          : (lista.length === 1 ? 'cliente pronto para receber' : 'clientes prontos para receber')}
                      </span>
                    </div>
                  )}
                  {(duplicados > 0 || invalidos.length > 0) && (
                    <div className="dp-notas">
                      {duplicados > 0 && (
                        <span className="dp-nota">
                          {numero(duplicados)} {duplicados === 1 ? 'repetido removido' : 'repetidos removidos'}
                        </span>
                      )}
                      {invalidos.length > 0 && (
                        <button type="button" className="dp-nota warn" onClick={() => setVerInvalidos((v) => !v)}>
                          {numero(invalidos.length)} com número inválido · {verInvalidos ? 'esconder' : 'ver'}
                        </button>
                      )}
                    </div>
                  )}
                  {verInvalidos && invalidos.length > 0 && (
                    <div className="dp-invalidos">
                      {invalidos.slice(0, 50).map((i) => <span key={`${i.indice}-${i.phone}`}>{i.phone || '(vazio)'} — {i.motivo}</span>)}
                      {invalidos.length > 50 && <span>e mais {numero(invalidos.length - 50)}</span>}
                    </div>
                  )}

                  <ListaDeDestinatarios
                    lista={lista}
                    onChange={setLista}
                    onColunas={setColunas}
                    onErro={setErroLista}
                    parsear={(t) => parsearNumerosComPais(t, pais.dial_code, { aceitaGrupos: !oficial })}
                    antesDoTexto={(
                      <button
                        type="button"
                        className="dp-pais"
                        onClick={() => setEscolhendoPais(true)}
                        aria-label={`País dos números: ${pais.dial_code}`}
                      >
                        <span>{pais.flag}</span><span>{pais.dial_code}</span>
                      </button>
                    )}
                    placeholderColar={oficial
                      ? 'Um por linha, ou separados por vírgula.\n11999998888, Ana Souza\n11977776666'
                      : 'Um por linha, ou separados por vírgula.\n11999998888, Ana Souza\nGrupos no formato 120363000000000000@g.us'}
                  />
                  {erroLista && <p className="dp-erro">{erroLista}</p>}
                  <button
                    type="button"
                    className="dp-btn pri inicio"
                    disabled={!lista.length}
                    onClick={() => setPasso(2)}
                  >
                    Continuar para a mensagem
                  </button>
                </Passo>

                <Passo
                  n={2}
                  titulo="Mensagem"
                  estado={estado(2)}
                  resumo={passo > 2 && resumoDaMensagem}
                  onAbrir={() => setPasso(2)}
                >
                  {testeLivre ? (
                    <TextoLivreDeTeste
                      api={api}
                      secret={secret}
                      suspensao={suspensaoOficial}
                      linkRecarga={linkRecarga}
                      onVoltar={() => setModoLivre(false)}
                    />
                  ) : oficial ? (
                    <EscolhaDeModelo
                      api={api}
                      secret={secret}
                      nome={modelo.nome}
                      valores={modelo.valores}
                      onChange={setModelo}
                      colunas={colunas}
                      exemplo={primeiro}
                      onErro={setErroModelos}
                    />
                  ) : (
                    <MensagemDeTexto
                      texto={texto}
                      onTexto={setTexto}
                      arquivo={arquivo}
                      onArquivo={setArquivo}
                      colunas={colunas}
                      exemplo={primeiro}
                    />
                  )}
                  {erroModelos && oficial && !testeLivre && !modelo.nome && <p className="dp-erro">{erroModelos}</p>}
                  {!testeLivre && (
                    <div className="dp-linha-botoes dp-passo2-acoes">
                      <button
                        type="button"
                        className="dp-btn pri"
                        disabled={!pronto}
                        onClick={() => setPasso(3)}
                      >
                        Revisar disparo
                      </button>
                      {oficial && (
                        <button type="button" className="dp-btn fantasma dp-link-livre" onClick={() => setModoLivre(true)}>
                          Escrever mensagem livre (teste)
                        </button>
                      )}
                    </div>
                  )}
                </Passo>

                <Passo n={3} titulo="Revisar e disparar" estado={estado(3)}>
                  <div className="dp-revisao">
                    <div><span>Para</span><b>{para}</b></div>
                    <div><span>Mensagem</span><b>{mensagemNaRevisao}</b></div>
                    <div>
                      <span>Quando sai</span>
                      <b>
                        {foraDoExpediente
                          ? 'No próximo horário de envio configurado, no ritmo do seu plano'
                          : 'Agora, no ritmo do seu plano e dentro do horário de envio configurado'}
                      </b>
                    </div>
                  </div>
                  <TesteNoSeuNumero
                    api={api}
                    secret={secret}
                    mensagem={mensagemDeTeste}
                    arquivo={arquivo}
                    oficial={oficial}
                    desabilitado={!pronto}
                  />
                </Passo>
              </div>

              <div ref={recibo}>
                {testeLivre ? (
                  <aside className="dp-recibo informativo" aria-label="Modo de teste">
                    <h3>Modo de teste</h3>
                    <p className="dp-extra">
                      O texto livre vai só para os números do teste, um de cada vez, sem custo. Nada é disparado para a
                      lista — o disparo da API Oficial continua sendo com modelo aprovado.
                    </p>
                    <button type="button" className="dp-btn" onClick={() => setModoLivre(false)}>
                      Voltar para o modelo
                    </button>
                  </aside>
                ) : (
                <ResumoDaConferencia
                  conferencia={conferencia}
                  // Lista e mensagem prontas mas a primeira resposta ainda não
                  // veio (espera de digitação): já é "conferindo", não "aparece quando…".
                  carregando={carregando || (pronto && !conferencia && !erroConferencia)}
                  erro={conferenciaDoServidor ? '' : erroConferencia}
                  linkRecarga={linkRecarga}
                  onSoOsQueCabem={setLimitarA}
                  para={para}
                  mensagem={mensagemCurta}
                  rotuloDaAcao={rotuloDaAcao}
                  podeAgir={podeAgir}
                  onAgir={abrirConfirmacao}
                  dicaDaAcao="Revise o disparo para liberar o botão."
                  limitadoA={limitarA}
                  onDesfazerLimite={() => setLimitarA(null)}
                  onConferirDeNovo={conferirDeNovo}
                  notaDoFiltro={temFiltro}
                />
                )}
              </div>
            </div>

            {!testeLivre && <div className="dp-barra">
              <div className="t">
                {barra.linha}
                <b className={barra.negativo ? 'neg' : ''}>{barra.valor}</b>
              </div>
              <button type="button" className="dp-btn pri" disabled={!barra.habilitado} onClick={barra.acao}>
                {barra.botao}
              </button>
            </div>}
          </>
        ) : (
          <>
            {emFoco ? (
              <PainelDoDisparo
                key={String(emFoco)}
                api={api}
                secret={secret}
                disparoId={String(emFoco)}
                linkRecarga={linkRecarga}
                onMudou={atualizarNaLista}
              />
            ) : carregouDisparos ? (
              <div className="dp-vazio">
                <b>Nenhum disparo ainda</b>
                <span>Os disparos que você fizer aparecem aqui, com o andamento de cada mensagem.</span>
                <button type="button" className="dp-btn pri" onClick={() => setAba('novo')}>Fazer o primeiro disparo</button>
              </div>
            ) : (
              <p className="dp-extra">Carregando os disparos…</p>
            )}
            <ListaDeDisparos disparos={disparos} selecionado={emFoco} onSelecionar={setSelecionado} />
          </>
        )}
      </div>

      <Dialog
        open={confirmando}
        onClose={() => { if (!enviando) setConfirmando(false) }}
        PaperProps={{ className: 'dp', style: { ...tokensDoDisparo(theme), borderRadius: 18 } }}
      >
        <div className="dp-dialogo">
          <h3>Confirmar o disparo</h3>
          <p>
            {oficial
              ? `Vou disparar ${numero(quantos)} mensagens do modelo ${modelo.nome}, custo de até ${reais(conferencia?.custo?.total)}.`
              : `Vou disparar ${numero(quantos)} mensagens de texto${nomeArquivo ? ` com ${nomeArquivo}` : ''}, sem custo por mensagem.`}
          </p>
          <p className="dp-extra">
            Começa em até 1 minuto e continua mesmo com o navegador fechado. Dá para cancelar o que ainda não saiu.
          </p>
          {erroCriacao && <p className="dp-erro" role="alert">{erroCriacao}</p>}
          <div className="dp-linha-botoes" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="dp-btn" onClick={() => setConfirmando(false)} disabled={enviando}>
              Voltar
            </button>
            <button type="button" className="dp-btn pri" onClick={criar} disabled={enviando}>
              {enviando ? 'Disparando…' : 'Disparar agora'}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  )
}
